import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, IndexTable, Text } from "@shopify/polaris";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import agent from "../../app/api/agent";
import { formatLocalDateTime } from "./utils/dateTime";
import { AdminPagedResult, AdminStoreSyncLog } from "../../app/models/Admin";
import SyncServiceMaintenancePanel from "./SyncServiceMaintenancePanel";
import { syncLogStatus } from "./utils/syncLogStatus";
import "./admin-console.css";

type AdminConsoleContext = {
  token: string;
  onUnauthorized: () => void;
};

const pageSize = 50;

export default function AdminSyncLogsPage() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [result, setResult] = useState<AdminPagedResult<AdminStoreSyncLog> | null>(null);
  const [loading, setLoading] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const page = Math.max(1, Number(searchParams.get("page") || "1") || 1);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await agent.Admin.syncLogs(token, page, pageSize);
      setResult(response);
    } catch {
      setError("Unable to load sync logs.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [token, page, onUnauthorized]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = result ? Math.max(1, Math.ceil(result.totalCount / result.pageSize)) : 1;

  const setPage = (nextPage: number) => {
    setSearchParams({ page: String(nextPage) });
  };

  const handleDeleteLog = async (log: AdminStoreSyncLog) => {
    const confirmed = window.confirm(`Delete sync log ${log.id}? This will also delete all related error records.`);
    if (!confirmed) return;

    setDeletingLogId(log.id);
    setError(null);

    try {
      await agent.Admin.deleteSyncLog(token, log.id);
      await loadLogs();
    } catch {
      setError(`Unable to delete sync log ${log.id}.`);
    } finally {
      setDeletingLogId(null);
    }
  };

  return (
    <Card>
      <div className="admin-sync-logs">
        <SyncServiceMaintenancePanel token={token} onUnauthorized={onUnauthorized} />
        <div className="admin-console-toolbar">
          <Text as="p" variant="bodyMd">All stores sync logs ordered by In Progress DESC, ID DESC.</Text>
          <Button onClick={loadLogs} loading={loading}>Refresh logs</Button>
        </div>
        <Text as="p" tone="subdued">
          Shopify exports can run across stores while local bulk imports run one at a time.
          Queued bulk jobs shown above have not been submitted yet; this table shows started syncs.
          Shopify statuses are last reported by the worker. Refresh logs to reload them.
        </Text>
        {error ? <Text as="p" tone="critical">{error}</Text> : null}
        {loading ? (
          <Text as="p">Loading sync logs...</Text>
        ) : !result || result.items.length === 0 ? (
          <Text as="p">No sync logs found.</Text>
        ) : (
          <IndexTable
            resourceName={{ singular: "sync log", plural: "sync logs" }}
            itemCount={result.items.length}
            selectable={false}
            headings={[
              { title: "ID" },
              { title: "Store" },
              { title: "Object" },
              { title: "Count" },
              { title: "Shopify" },
              { title: "Local processing" },
              { title: "Started" },
              { title: "Completed" },
              { title: "Errors" },
              { title: "Actions" },
            ]}
          >
            {result.items.map((log, index) => {
              const status = syncLogStatus(log);
              return (
              <IndexTable.Row id={`${log.id}`} key={log.id} position={index}>
                <IndexTable.Cell>{log.id}</IndexTable.Cell>
                <IndexTable.Cell>
                  {log.shopId ? (
                    <Button variant="plain" onClick={() => navigate(`/pf-console/stores/${log.shopId}`)}>{log.shopName || log.shopDomain || `Shop ${log.shopId ?? "-"}`}</Button>
                  ) : (
                    <Text as="span">{log.shopName || log.shopDomain || "-"}</Text>
                  )}
                </IndexTable.Cell>
                <IndexTable.Cell>{log.syncObject || "Unknown"} / {log.propertyName || "-"}</IndexTable.Cell>
                <IndexTable.Cell>{(log.objectCount ?? 0).toLocaleString()}</IndexTable.Cell>
                <IndexTable.Cell>
                  <div style={{ display: "grid", gap: "0.25rem" }}>
                    <Badge tone={status.shopify.tone}>{status.shopify.label}</Badge>
                    {log.bulkOperationGid ? (
                      <Text as="span" variant="bodySm" tone="subdued">
                        {`Operation ${log.bulkOperationGid.split('/').pop()}`}
                      </Text>
                    ) : null}
                  </div>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <div style={{ display: "grid", gap: "0.25rem" }}>
                    <Badge tone={status.local.tone}>{status.local.label}</Badge>
                    {log.localOperationProgress ? <Text as="span" variant="bodySm" tone="subdued">{log.localOperationProgress}</Text> : null}
                  </div>
                </IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(log.startedAt)}</IndexTable.Cell>
                <IndexTable.Cell>{formatLocalDateTime(log.completedAt)}</IndexTable.Cell>
                <IndexTable.Cell>
                  {log.hasErrors ? (
                    <Button variant="plain" onClick={() => navigate(`/pf-console/sync-logs/${log.id}/errors`)}>
                      {`${log.errorCount || 0} error(s)`}
                    </Button>
                  ) : (
                    <Text as="span" tone="subdued">0</Text>
                  )}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Button
                    variant="plain"
                    tone="critical"
                    disabled={log.inProgress || deletingLogId === log.id}
                    onClick={() => handleDeleteLog(log)}
                  >
                    Delete
                  </Button>
                </IndexTable.Cell>
              </IndexTable.Row>
              );
            })}
          </IndexTable>
        )}
        <div className="admin-sync-logs__pagination">
          <Button disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Previous</Button>
          <Text as="span">Page {page} of {totalPages}</Text>
          <Button disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </Card>
  );
}
